import {
  aspectRatioMap,
  backgroundMap,
  borderColorMap,
  borderRadiusMap,
  borderWidthMap,
  overflowMap,
  overflowXMap,
  overflowYMap,
  paddingBottomMap,
  paddingHorizontalMap,
  paddingLeftMap,
  paddingMap,
  paddingRightMap,
  paddingTopMap,
  paddingVerticalMap,
} from "@/constants/designSystemScale";
import {
  AspectRatioType,
  BackgroundType,
  BorderRadiusType,
  OverflowType,
  OverflowXType,
  OverflowYType,
  PositionType,
  PaddingType,
  BorderWidthType,
  BorderColorType,
} from "@/types/designSystem.types";
import React from "react";

type BoxSizingType<T extends string> =
  | `${T}-${"auto" | "full" | "screen" | "half-screen" | `${"" | number}xl`}`
  | `${T}-min`
  | `${T}-${number}`
  | `${T}-${number}/${number}`;

export type ResponsiveBoxSizingType<T extends string> =
  | [BoxSizingType<T>, ...`${string}:${BoxSizingType<T>}`[]] // this ensures first value of Array is of type1 and rest are of type2
  | BoxSizingType<T>;

type BoxSizingTypeWithoutAuto<T extends string> =
  | `${T}-${"full" | "screen" | "fit" | `${"" | number}xl`}`
  | `${T}-min`
  | `${T}-${number}`
  | `${T}-${number}/${number}`;

type PlacementPositionGeneric<T extends string> =
  | `${T}-${number}`
  | `${T}-${"auto" | "full"}`
  | `${T}-${number}/${number}`;

interface BoxProps {
  padding?: PaddingType;
  paddingTop?: PaddingType;
  paddingBottom?: PaddingType;
  paddingLeft?: PaddingType;
  paddingRight?: PaddingType;
  paddingHorizontal?: PaddingType;
  paddingVertical?: PaddingType;
  position?: PositionType;
  top?: PlacementPositionGeneric<"top">;
  bottom?: PlacementPositionGeneric<"bottom">;
  left?: PlacementPositionGeneric<"left">;
  right?: PlacementPositionGeneric<"right">;
  aspectRatio?: AspectRatioType;
  width?: ResponsiveBoxSizingType<"w">;
  height?: ResponsiveBoxSizingType<"h">;
  maxWidth?: BoxSizingTypeWithoutAuto<"max-w">;
  maxHeight?: BoxSizingTypeWithoutAuto<"max-h">;
  minWidth?: BoxSizingTypeWithoutAuto<"min-w">;
  minHeight?: BoxSizingTypeWithoutAuto<"min-h">;
  borderRadius?: BorderRadiusType;
  borderWidth?: BorderWidthType | null;
  borderColor?: BorderColorType;
  overflow?: OverflowType;
  overflowX?: OverflowXType;
  overflowY?: OverflowYType;
  background?: BackgroundType;
  hideScrollbar?: boolean;
  children?: React.ReactNode;
  dataTestId?: string;
  fillInFlex?: boolean;
  // to hide focus outline children of the box
  hideFocusOutline?: boolean;
}

const getResponsiveClass = (value: string | Array<string>) => {
  if (typeof value === "string") {
    return `${value}`;
  } else if (Array.isArray(value)) {
    return value.join(" ");
  }
  return "";
};

export const Box = React.forwardRef<HTMLDivElement, BoxProps>(
  (
    {
      padding,
      paddingTop,
      paddingBottom,
      paddingLeft,
      paddingRight,
      paddingHorizontal,
      paddingVertical,
      position,
      top,
      bottom,
      left,
      right,
      aspectRatio,
      width,
      height,
      maxWidth,
      maxHeight,
      minWidth,
      minHeight,
      borderRadius,
      borderColor,
      borderWidth,
      hideScrollbar,
      overflow,
      overflowX,
      overflowY,
      background,
      children,
      dataTestId = "box",
      fillInFlex = false,
      hideFocusOutline = false,
    },
    ref,
  ) => {
    const classes = [
      padding && paddingMap[padding].class,
      paddingTop && paddingTopMap[paddingTop].class,
      paddingBottom && paddingBottomMap[paddingBottom].class,
      paddingLeft && paddingLeftMap[paddingLeft].class,
      paddingRight && paddingRightMap[paddingRight].class,
      paddingHorizontal && paddingHorizontalMap[paddingHorizontal].class,
      paddingVertical && paddingVerticalMap[paddingVertical].class,
      position ? position : "",
      position && position !== "static" && top,
      position && position !== "static" && bottom,
      position && position !== "static" && left,
      position && position !== "static" && right,
      aspectRatio && aspectRatioMap[aspectRatio].class,
      width && getResponsiveClass(width),
      height && getResponsiveClass(height),
      maxWidth,
      maxHeight,
      minWidth,
      minHeight,
      hideScrollbar && "hide-scrollbar",
      borderRadius && borderRadiusMap[borderRadius].class,
      borderColor && borderColorMap[borderColor].class,
      borderWidth && borderWidthMap[borderWidth].class,
      background && backgroundMap[background].class,
      overflow && overflowMap[overflow].class,
      overflowX && overflowXMap[overflowX].class,
      overflowY && overflowYMap[overflowY].class,
      fillInFlex && "flex-1",
      hideFocusOutline && "hide-focus-outline",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div className={classes} data-testid={dataTestId} ref={ref}>
        {children}
      </div>
    );
  },
);

Box.displayName = "Box";
